import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { pdf } from '@react-pdf/renderer';
import { apiFetch } from '../../../shared/lib/apiClient';
import { useAuth } from '../../auth/context/AuthContext';
import InvoicePDF, { DEFAULT_TEMPLATE_CONFIG } from './InvoicePDF';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STATUS_CONFIG = {
  draft:   { label: 'Draft',    bg: 'bg-zinc-100',   text: 'text-zinc-600',   dot: 'bg-zinc-400' },
  sent:    { label: 'Sent',     bg: 'bg-blue-50',    text: 'text-blue-700',   dot: 'bg-blue-400' },
  partial: { label: 'Partial',  bg: 'bg-amber-50',   text: 'text-amber-700',  dot: 'bg-amber-400' },
  overdue: { label: 'Overdue',  bg: 'bg-red-50',     text: 'text-red-700',    dot: 'bg-red-400' },
  paid:    { label: 'Paid',     bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  voided:  { label: 'Voided',   bg: 'bg-zinc-50',    text: 'text-zinc-400',   dot: 'bg-zinc-300' },
};

const TERMS_OPTIONS = [
  { value: 'due_on_receipt', label: 'Due on receipt' },
  { value: 'net_10', label: 'Net 10' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_45', label: 'Net 45' },
  { value: 'net_60', label: 'Net 60' },
  { value: 'net_90', label: 'Net 90' },
];

const TERMS_TO_DAYS = { due_on_receipt: 0, net_10: 10, net_15: 15, net_30: 30, net_45: 45, net_60: 60, net_90: 90 };

const PAYMENT_METHODS = [
  { value: 'ach', label: 'ACH / Bank transfer' },
  { value: 'check', label: 'Check' },
  { value: 'wire', label: 'Wire transfer' },
  { value: 'credit_card', label: 'Credit card' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

// ---------------------------------------------------------------------------
// Dynamic Column Support
// ---------------------------------------------------------------------------
const DEFAULT_COLUMNS = [
  { key: 'description', label: 'Description', type: 'text', required: true },
  { key: 'quantity', label: 'Quantity', type: 'number', default: 1 },
  { key: 'unit_price', label: 'Unit Price', type: 'currency', default: 0 },
  { key: 'amount', label: 'Amount', type: 'currency', formula: 'quantity * unit_price' },
];

const COLUMN_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Checkbox' },
];

const FORMULA_CAPABLE_TYPES = new Set(['number', 'currency', 'percent']);

function isComputed(col) {
  return typeof col.formula === 'string' && col.formula.trim().length > 0;
}

function normalizeColumns(columns) {
  if (!columns) return DEFAULT_COLUMNS;
  return columns.map(col => col.type === 'computed' ? { ...col, type: 'currency' } : col);
}

/** Translate user-friendly operators to JS (e.g. ^ → **) */
function prepareFormula(formula) {
  return formula.replace(/\^/g, '**');
}

function evaluateFormula(formula, values) {
  const keys = Object.keys(values);
  const vals = Object.values(values).map(v => Number(v) || 0);
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(...keys, `"use strict"; return (${prepareFormula(formula)});`);
    const result = fn(...vals);
    return isFinite(result) ? Math.round(result * 100) / 100 : 0;
  } catch {
    return 0;
  }
}

/** Try to compile & run a formula with test values. Returns null if valid, friendly error string if not. */
function validateFormula(formula, availableVars) {
  if (!formula || !formula.trim()) return 'Formula is empty';
  // Check for unknown variable names
  const varNames = new Set(availableVars.map(v => v.key));
  const words = formula.match(/[a-zA-Z_]\w*/g) || [];
  for (const w of words) {
    if (!varNames.has(w)) return `Unknown variable "${w}"`;
  }
  const testValues = {};
  for (const v of availableVars) testValues[v.key] = 1;
  const keys = Object.keys(testValues);
  const vals = Object.values(testValues);
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(...keys, `"use strict"; return (${prepareFormula(formula)});`);
    const result = fn(...vals);
    if (typeof result !== 'number' || !isFinite(result)) return 'Result is not a valid number';
    return null;
  } catch {
    // Friendly messages for common mistakes
    const trimmed = formula.trim();
    if (/[+\-*/^%]$/.test(trimmed)) return 'Incomplete — ends with an operator';
    if (/^[+*/^%]/.test(trimmed)) return 'Incomplete — starts with an operator';
    if ((trimmed.match(/\(/g) || []).length !== (trimmed.match(/\)/g) || []).length) return 'Mismatched parentheses';
    if (/[a-zA-Z_]\w*\s+[a-zA-Z_]\w*/.test(trimmed)) return 'Missing operator between variables';
    return 'Invalid formula — check your syntax';
  }
}

function buildEmptyLine(columns) {
  const line = {};
  for (const col of columns) {
    if (isComputed(col)) continue;
    if (col.type === 'number' || col.type === 'currency' || col.type === 'percent') {
      line[col.key] = col.default !== undefined ? col.default : 0;
    } else if (col.type === 'boolean') {
      line[col.key] = false;
    } else {
      line[col.key] = col.default !== undefined ? col.default : '';
    }
  }
  return line;
}

// Fixed pixel width per column type
function colSpec(col) {
  if (col.key === 'description') return 'minmax(100px, 1fr)';
  if (col.type === 'text') return 'minmax(60px, max-content)';
  if (col.type === 'boolean') return '60px';
  if (col.type === 'date') return '110px';
  if (col.type === 'percent') return '80px';
  if (col.type === 'currency') return '100px';
  if (col.type === 'number') return '70px';
  return '80px';
}

function gridCols(columns, extra) {
  const cols = columns.map((col) => colSpec(col));
  return cols.concat(extra ? [extra] : []).join(' ');
}

function formatCellValue(col, value) {
  if (col.type === 'currency') return fmt(value);
  if (col.type === 'percent') return value != null ? `${value}%` : '—';
  if (col.type === 'boolean') return value ? 'Yes' : 'No';
  if (col.type === 'date') return value ? fmtDate(value) : '—';
  if (col.type === 'number') return value != null ? String(value) : '—';
  return value ?? '—';
}

function computeRowValues(columns, lineItem) {
  const computed = {};
  for (const col of columns) {
    if (isComputed(col)) {
      computed[col.key] = evaluateFormula(col.formula, { ...lineItem, ...computed });
    }
  }
  return computed;
}

function slugify(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `col_${Date.now()}`;
}

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Summary Cards
// ---------------------------------------------------------------------------
const SummaryCards = ({ summary, loaded }) => {
  const cards = [
    { label: 'Outstanding', value: fmt(summary.totalOutstanding), sub: `${(summary.counts?.sent || 0) + (summary.counts?.partial || 0)} invoices` },
    { label: 'Overdue', value: fmt(summary.totalOverdue), sub: `${summary.counts?.overdue || 0} invoices`, alert: summary.totalOverdue > 0 },
    { label: 'Collected (all time)', value: fmt(summary.totalRevenue), sub: `${summary.counts?.paid || 0} paid` },
    { label: 'Draft', value: summary.counts?.draft || 0, sub: 'awaiting send' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {cards.map((c, i) => (
        <div
          key={c.label}
          className="bg-white rounded-lg border border-zinc-200 p-4 transition-all duration-500"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(8px)', transitionDelay: `${300 + i * 80}ms` }}
        >
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">{c.label}</p>
          <p className={`text-xl font-semibold ${c.alert ? 'text-red-600' : 'text-zinc-900'}`}>{c.value}</p>
          <p className="text-xs text-zinc-400 mt-0.5">{c.sub}</p>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Column Editor (inline customization) — flat list, per-column formulas
// ---------------------------------------------------------------------------
const ColumnEditor = ({ columns, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const newNameRef = useRef(null);
  const [expandedFormula, setExpandedFormula] = useState(null);
  const [formulaError, setFormulaError] = useState(null); // error string or null

  const descCol = columns[0];
  const editableCols = columns.slice(1);

  // Variables available for a column's formula: numeric columns that appear BEFORE this column
  // (so computed columns can reference each other as long as dependencies flow top-to-bottom)
  const getFormulaVars = (forColumnKey) => {
    const vars = [];
    for (const c of columns) {
      if (c.key === forColumnKey) break; // only columns above this one
      if (FORMULA_CAPABLE_TYPES.has(c.type) && c.label?.trim()) {
        vars.push(c);
      }
    }
    return vars;
  };

  const updateDescLabel = (label) => {
    onChange([{ ...descCol, label }, ...editableCols]);
  };

  const updateCol = (idx, field, value) => {
    const updated = [...editableCols];
    const oldKey = updated[idx].key;
    updated[idx] = { ...updated[idx], [field]: value };

    if (field === 'label' && !updated[idx]._keyEdited) {
      const newKey = slugify(value);
      updated[idx].key = newKey;
      // Update ALL formulas across ALL columns that reference the old key
      if (oldKey !== newKey) {
        for (let i = 0; i < updated.length; i++) {
          if (updated[i].formula && updated[i].formula.includes(oldKey)) {
            updated[i] = { ...updated[i], formula: updated[i].formula.replace(new RegExp(`\\b${oldKey}\\b`, 'g'), newKey) };
          }
        }
      }
    }

    // If type changed to non-formula-capable, clear formula
    if (field === 'type' && !FORMULA_CAPABLE_TYPES.has(value)) {
      const { formula, ...rest } = updated[idx];
      updated[idx] = rest;
      if (expandedFormula === updated[idx].key) setExpandedFormula(null);
    }

    onChange([descCol, ...updated]);
  };

  const moveCol = (idx, dir) => {
    const updated = [...editableCols];
    const target = idx + dir;
    if (target < 0 || target >= updated.length) return;
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    onChange([descCol, ...updated]);
  };

  const removeCol = (idx) => {
    const removedKey = editableCols[idx].key;
    const updated = editableCols.filter((_, i) => i !== idx);
    // Clean references from all formulas
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].formula && updated[i].formula.includes(removedKey)) {
        updated[i] = { ...updated[i], formula: updated[i].formula.replace(new RegExp(`\\b${removedKey}\\b`, 'g'), '').trim() };
      }
    }
    if (expandedFormula === removedKey) setExpandedFormula(null);
    onChange([descCol, ...updated]);
  };

  const updateColFormula = (idx, formula) => {
    const updated = [...editableCols];
    if (formula) {
      updated[idx] = { ...updated[idx], formula };
      // Live-validate
      const vars = getFormulaVars(updated[idx].key);
      setFormulaError(validateFormula(formula, vars));
    } else {
      const { formula: _, ...rest } = updated[idx];
      updated[idx] = rest;
      setFormulaError('Formula is empty');
    }
    onChange([descCol, ...updated]);
  };

  const insertVar = (colIdx, varKey) => {
    const current = editableCols[colIdx].formula || '';
    const needSpace = current.length > 0 && !/[\s(+\-*/]$/.test(current);
    updateColFormula(colIdx, current + (needSpace ? ' ' : '') + varKey);
  };

  const toggleFormula = (col) => {
    if (expandedFormula === col.key) {
      setExpandedFormula(null);
      setFormulaError(null);
    } else {
      setExpandedFormula(col.key);
      // Validate current formula on open
      const vars = getFormulaVars(col.key);
      setFormulaError(col.formula ? validateFormula(col.formula, vars) : null);
    }
  };

  const commitAdd = () => {
    const label = newName.trim();
    if (!label) return;
    const key = slugify(label);
    const updated = [...editableCols, { key, label, type: 'number', default: 0 }];
    onChange([descCol, ...updated]);
    setNewName('');
    setAdding(false);
  };

  const startAdd = () => {
    setAdding(true);
    setNewName('');
    requestAnimationFrame(() => newNameRef.current?.focus());
  };

  const cancelAdd = () => {
    setNewName('');
    setAdding(false);
  };

  return (
    <div className="mb-4 border border-zinc-300 rounded-lg overflow-hidden">
      <div className="bg-zinc-50 p-3 space-y-1">
        {/* Description (locked, can rename) */}
        <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-white border border-zinc-200">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400 shrink-0"><rect x="3" y="7" width="10" height="7" rx="1.5" /><path d="M5 7V5a3 3 0 016 0v2" /></svg>
          <input type="text" value={descCol.label} onChange={(e) => updateDescLabel(e.target.value)}
            className="flex-1 text-sm font-medium bg-transparent outline-none text-zinc-800 placeholder-zinc-400" placeholder="Label" />
          <span className="text-[10px] text-zinc-500 font-mono bg-zinc-100 px-1.5 py-0.5 rounded shrink-0">text</span>
        </div>

        {/* All other columns */}
        {editableCols.map((col, idx) => {
          const canFormula = FORMULA_CAPABLE_TYPES.has(col.type);
          const hasFormula = isComputed(col);
          const isExpanded = expandedFormula === col.key;
          const vars = canFormula ? getFormulaVars(col.key) : [];

          return (
            <div key={col.key}>
              <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-white border border-zinc-200 group">
                <div className="flex flex-col shrink-0 -my-1">
                  <button type="button" onClick={() => moveCol(idx, -1)} disabled={idx === 0}
                    className="text-zinc-400 hover:text-zinc-700 disabled:text-zinc-200 transition-colors" title="Move up">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 10l4-4 4 4" /></svg>
                  </button>
                  <button type="button" onClick={() => moveCol(idx, 1)} disabled={idx === editableCols.length - 1}
                    className="text-zinc-400 hover:text-zinc-700 disabled:text-zinc-200 transition-colors" title="Move down">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6l4 4 4-4" /></svg>
                  </button>
                </div>
                <input
                  type="text"
                  value={col.label}
                  onChange={(e) => updateCol(idx, 'label', e.target.value)}
                  className="flex-1 text-sm font-medium bg-transparent outline-none min-w-0 text-zinc-800 placeholder-zinc-400"
                  placeholder="Column name"
                />
                <select
                  value={col.type}
                  onChange={(e) => updateCol(idx, 'type', e.target.value)}
                  className="text-xs text-zinc-600 bg-zinc-100 border border-zinc-200 rounded px-2 py-1 outline-none cursor-pointer shrink-0"
                >
                  {COLUMN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                {canFormula && (
                  <button
                    type="button"
                    onClick={() => toggleFormula(col)}
                    className={`shrink-0 px-1.5 py-0.5 text-[10px] font-bold rounded transition-colors border ${
                      hasFormula
                        ? 'bg-zinc-900 text-white border-zinc-900'
                        : isExpanded
                          ? 'bg-zinc-200 text-zinc-700 border-zinc-300'
                          : 'bg-zinc-50 text-zinc-400 border-zinc-200 hover:text-zinc-700 hover:border-zinc-400'
                    }`}
                    title={hasFormula ? 'Edit formula' : 'Add formula'}
                  >
                    fx
                  </button>
                )}
                <button type="button" onClick={() => removeCol(idx)}
                  className="text-zinc-300 hover:text-red-500 group-hover:text-zinc-400 transition-colors shrink-0" title="Remove">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                </button>
              </div>

              {/* Inline formula editor for this column */}
              {canFormula && isExpanded && (() => {
                const hasError = !!formulaError;
                const isEmpty = !col.formula?.trim();
                return (
                <div className={`ml-8 mr-3 mt-1 mb-1 p-2.5 bg-white border rounded-md space-y-2 ${hasError && !isEmpty ? 'border-red-300' : 'border-zinc-200'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-zinc-500 shrink-0">{col.label || col.key}</span>
                    <span className="text-xs text-zinc-400">=</span>
                    <input
                      type="text"
                      value={col.formula || ''}
                      onChange={(e) => updateColFormula(idx, e.target.value)}
                      className={`flex-1 text-sm font-mono text-zinc-800 bg-zinc-50 rounded px-2.5 py-1 outline-none border transition-colors ${
                        hasError && !isEmpty ? 'border-red-300 focus:border-red-500' : 'border-zinc-200 focus:border-zinc-500'
                      }`}
                      placeholder="e.g. quantity * unit_price"
                    />
                    {hasFormula && (
                      <button type="button" onClick={() => { updateColFormula(idx, ''); setExpandedFormula(null); setFormulaError(null); }}
                        className="text-zinc-400 hover:text-red-500 transition-colors shrink-0" title="Remove formula">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                      </button>
                    )}
                  </div>
                  {hasError && !isEmpty && (
                    <p className="text-[11px] text-red-500 ml-0.5">{formulaError}</p>
                  )}
                  <div className="flex items-center justify-between">
                    {vars.length > 0 ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] text-zinc-400 uppercase tracking-wider mr-0.5">Variables:</span>
                        {vars.map((v) => (
                          <button
                            key={v.key}
                            type="button"
                            onClick={() => insertVar(idx, v.key)}
                            className="px-2 py-0.5 text-xs font-mono bg-zinc-100 hover:bg-zinc-900 hover:text-white text-zinc-700 rounded-md transition-colors border border-zinc-200 hover:border-zinc-900"
                            title={`Append "${v.key}" to formula`}
                          >
                            {v.key}
                          </button>
                        ))}
                      </div>
                    ) : <div />}
                    <button
                      type="button"
                      onClick={() => { if (!hasError) { setExpandedFormula(null); setFormulaError(null); } }}
                      className={`flex items-center justify-center w-6 h-6 rounded transition-colors shrink-0 ${
                        hasError ? 'bg-zinc-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                      }`}
                      title={hasError ? formulaError : 'Done'}
                      disabled={hasError}
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8l4 4 6-6" /></svg>
                    </button>
                  </div>
                </div>
                );
              })()}
            </div>
          );
        })}

        {/* Add column */}
        {adding ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border-2 border-zinc-400">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-400 shrink-0"><path d="M8 3v10M3 8h10" /></svg>
            <input
              ref={newNameRef}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitAdd(); }
                if (e.key === 'Escape') cancelAdd();
              }}
              onBlur={() => { if (!newName.trim()) cancelAdd(); }}
              className="flex-1 text-sm font-medium bg-transparent outline-none text-zinc-800 placeholder-zinc-400 min-w-0"
              placeholder="Column name, then press Enter"
            />
            <button type="button" onClick={commitAdd} disabled={!newName.trim()}
              className="text-zinc-400 hover:text-emerald-600 disabled:text-zinc-200 transition-colors shrink-0" title="Add">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8l4 4 6-6" /></svg>
            </button>
            <button type="button" onClick={cancelAdd}
              className="text-zinc-400 hover:text-zinc-700 transition-colors shrink-0" title="Cancel">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
            </button>
          </div>
        ) : (
          <button type="button" onClick={startAdd}
            className="flex items-center gap-2 px-3 py-2 w-full text-sm text-zinc-500 hover:text-zinc-800 hover:bg-white rounded-md border border-dashed border-zinc-300 hover:border-zinc-400 transition-colors">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v10M3 8h10" /></svg>
            Add column
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Save-as-default Prompt
// ---------------------------------------------------------------------------
const SaveDefaultPrompt = ({ onSave, onSkip }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onSkip}>
    <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
      <h3 className="text-sm font-semibold text-zinc-900 mb-2">Save column layout?</h3>
      <p className="text-xs text-zinc-500 mb-5">You've customized your columns. Save this as your default layout for future invoices?</p>
      <div className="flex justify-end gap-2">
        <button onClick={onSkip} className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors">No, just this invoice</button>
        <button onClick={onSave} className="px-4 py-2 bg-zinc-900 text-white text-sm rounded-md hover:bg-zinc-800 transition-colors">Save as default</button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Live Invoice Preview — fluid layout, only table scrolls horizontally
// ---------------------------------------------------------------------------
function previewFmtCell(col, value) {
  if (col.type === 'currency') return fmt(value);
  if (col.type === 'percent') return value != null ? `${value}%` : '—';
  if (col.type === 'boolean') return value ? 'Yes' : 'No';
  if (col.type === 'date') return value ? fmtDate(value) : '—';
  if (col.type === 'number') return value != null ? String(value) : '—';
  return value ?? '—';
}

const InvoicePreview = ({ invoice, companyName, collapsed, onToggleCollapse, templateConfig }) => {
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggleCollapse}
        className="flex flex-col items-center gap-3 w-full py-4 bg-zinc-50 hover:bg-zinc-100 transition-colors cursor-pointer rounded-lg h-full"
        title="Expand preview"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-zinc-400 shrink-0">
          <path d="M6 3l5 5-5 5" />
        </svg>
        <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-[0.15em] select-none" style={{ writingMode: 'vertical-rl' }}>
          Preview
        </span>
      </button>
    );
  }

  const tc = { ...DEFAULT_TEMPLATE_CONFIG, ...templateConfig, colors: { ...DEFAULT_TEMPLATE_CONFIG.colors, ...templateConfig?.colors } };
  const displayName = tc.company_name_override || companyName;
  const cust = invoice.customers || {};
  const columns = normalizeColumns(invoice.column_config);
  const termsLabel = TERMS_OPTIONS.find(t => t.value === invoice.terms)?.label || invoice.terms || '—';
  const hasCompanyDetails = tc.show_company_details && (tc.company_address || tc.company_phone || tc.company_email || tc.company_website || tc.company_tax_id);

  // Font family mapping for preview (CSS-safe names)
  const fontMap = { Helvetica: 'Helvetica, Arial, sans-serif', 'Times-Roman': 'Times New Roman, serif', Courier: 'Courier New, monospace' };
  const fontFamily = fontMap[tc.font_family] || fontMap.Helvetica;

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div className="flex items-center px-3 py-2 border-b border-zinc-200 bg-zinc-50 rounded-t-lg gap-2 shrink-0">
        <button type="button" onClick={onToggleCollapse} className="text-zinc-400 hover:text-zinc-700 transition-colors" title="Collapse preview">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M10 3l-5 5 5 5" /></svg>
        </button>
        <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Preview</span>
      </div>

      {/* Invoice content */}
      <div className="flex-1 overflow-y-auto rounded-b-lg" style={{ backgroundColor: tc.colors.background }}>
        <div className="p-5" style={{ fontFamily, color: tc.colors.text }}>

          {/* Header */}
          <div className="flex justify-between items-start mb-5 gap-3">
            <div className="min-w-0">
              <span className="font-bold text-lg truncate block" style={{ color: tc.colors.primary }}>{displayName}</span>
              {hasCompanyDetails && (
                <div className="mt-1 space-y-0.5">
                  {tc.company_address && <div className="text-[10px] text-zinc-500">{tc.company_address}</div>}
                  {tc.company_phone && <div className="text-[10px] text-zinc-500">{tc.company_phone}</div>}
                  {tc.company_email && <div className="text-[10px] text-zinc-500">{tc.company_email}</div>}
                  {tc.company_website && <div className="text-[10px] text-zinc-500">{tc.company_website}</div>}
                  {tc.company_tax_id && <div className="text-[10px] text-zinc-500">Tax ID: {tc.company_tax_id}</div>}
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="font-bold text-2xl tracking-tight" style={{ color: tc.colors.primary }}>INVOICE</div>
              <div className="text-xs text-zinc-400 mt-0.5">{invoice.invoice_number}</div>
            </div>
          </div>

          <div className="border-b border-zinc-200 mb-5" />

          {/* Bill To + Meta */}
          <div className="flex justify-between mb-6 gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Bill To</div>
              {cust.display_name && <div className="font-semibold text-sm leading-relaxed" style={{ color: tc.colors.primary }}>{cust.display_name}</div>}
              {cust.company_name && cust.company_name !== cust.display_name && (
                <div className="text-zinc-500 text-xs leading-relaxed">{cust.company_name}</div>
              )}
              {cust.email && <div className="text-zinc-500 text-xs leading-relaxed">{cust.email}</div>}
              {cust.phone && <div className="text-zinc-500 text-xs leading-relaxed">{cust.phone}</div>}
            </div>
            <div className="shrink-0 space-y-1">
              {[
                ['Invoice Date', fmtDate(invoice.invoice_date)],
                ['Due Date', fmtDate(invoice.due_date)],
                ['Terms', termsLabel],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline gap-3 justify-end">
                  <span className="text-[11px] text-zinc-400">{label}</span>
                  <span className="text-xs font-semibold text-zinc-700 w-24 text-right">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Line Items Table */}
          <div className="mb-5 rounded-md border border-zinc-200 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
            <table className="w-full" style={{ minWidth: `${Math.max(300, columns.length * 100)}px` }}>
              <thead>
                <tr style={{ backgroundColor: tc.colors.accent }}>
                  {columns.map(col => (
                    <th key={col.key} className="px-3 py-2 text-[10px] font-semibold text-zinc-500 uppercase tracking-wide whitespace-nowrap" style={{ textAlign: col.type === 'text' ? 'left' : 'right' }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(invoice.line_items || []).map((li, i) => (
                  <tr key={li.id ?? i} style={i % 2 === 1 ? { backgroundColor: `${tc.colors.accent}40` } : {}}>
                    {columns.map(col => (
                      <td key={col.key} className="px-3 py-2 text-xs whitespace-nowrap" style={{ textAlign: col.type === 'text' ? 'left' : 'right', color: tc.colors.text }}>
                        {previewFmtCell(col, li[col.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-5">
            <div className="w-52 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Subtotal</span>
                <span style={{ color: tc.colors.text }}>{fmt(invoice.subtotal)}</span>
              </div>
              {Number(invoice.tax_amount) > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Tax ({(Number(invoice.tax_rate) * 100).toFixed(1)}%)</span>
                  <span style={{ color: tc.colors.text }}>{fmt(invoice.tax_amount)}</span>
                </div>
              )}
              <div className="border-b border-zinc-200 !my-1.5" />
              <div className="flex justify-between text-sm font-bold">
                <span style={{ color: tc.colors.primary }}>Total</span>
                <span style={{ color: tc.colors.primary }}>{fmt(invoice.total_amount)}</span>
              </div>
              {invoice.amount_paid > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-emerald-600">Amount Paid</span>
                  <span className="text-emerald-600">-{fmt(invoice.amount_paid)}</span>
                </div>
              )}
              {invoice.balance_due > 0 && invoice.amount_paid > 0 && (
                <>
                  <div className="border-b border-zinc-200 !my-1.5" />
                  <div className="flex justify-between text-sm font-bold">
                    <span style={{ color: tc.colors.primary }}>Balance Due</span>
                    <span style={{ color: tc.colors.primary }}>{fmt(invoice.balance_due)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          {tc.show_notes_section && invoice.notes && (
            <div className="pt-4 border-t border-zinc-100">
              <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Notes</div>
              <div className="text-xs text-zinc-600 whitespace-pre-wrap leading-relaxed">{invoice.notes}</div>
            </div>
          )}

          {/* Footer */}
          {tc.show_footer_section && invoice.footer_note && (
            <div className="pt-3">
              <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Terms</div>
              <div className="text-xs text-zinc-600 whitespace-pre-wrap leading-relaxed">{invoice.footer_note}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Invoice Form (create / edit) — dynamic columns
// ---------------------------------------------------------------------------
const InvoiceForm = ({ invoice, customers, onSave, onCancel, saving, onCustomerCreated, savedColumns, companyName, templates }) => {
  const isEdit = !!invoice;
  const [customerId, setCustomerId] = useState(invoice?.customer_id || '');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustFirstName, setNewCustFirstName] = useState('');
  const [newCustLastName, setNewCustLastName] = useState('');
  const [newCustCompany, setNewCustCompany] = useState('');
  const [newCustEmail, setNewCustEmail] = useState('');
  const [newCustPhone, setNewCustPhone] = useState('');
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(invoice?.invoice_number || '');
  const [invoiceDate, setInvoiceDate] = useState(invoice?.invoice_date || new Date().toISOString().split('T')[0]);
  const [terms, setTerms] = useState(invoice?.terms || 'net_30');
  const [dueDate, setDueDate] = useState(invoice?.due_date || addDays(new Date().toISOString().split('T')[0], 30));
  const [taxRate, setTaxRate] = useState(invoice?.tax_rate ? (Number(invoice.tax_rate) * 100).toFixed(2) : '0');
  const [notes, setNotes] = useState(invoice?.notes || '');
  const [footerNote, setFooterNote] = useState(invoice?.footer_note || '');

  // Template selection
  const defaultTemplate = (templates || []).find(t => t.is_default);
  const [selectedTemplateId, setSelectedTemplateId] = useState(invoice?.template_id || defaultTemplate?.id || '');
  const activeTemplate = (templates || []).find(t => t.id === selectedTemplateId);
  const activeTemplateConfig = activeTemplate?.template_config || null;

  // Column config (normalize old 'computed' type on load)
  const [columns, setColumns] = useState(() => normalizeColumns(invoice?.column_config || savedColumns || null));
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(480);
  const [poExtracting, setPOExtracting] = useState(false);
  const [poError, setPOError] = useState(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(480);
  const containerRef = useRef(null);
  const poFileRef = useRef(null);

  const MIN_FORM_WIDTH = 480;
  const MIN_PREVIEW_WIDTH = 280;

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = previewWidth;

    const onMove = (ev) => {
      if (!draggingRef.current) return;
      // Dragging left (negative deltaX) = wider panel
      const delta = startXRef.current - ev.clientX;
      const containerW = containerRef.current?.offsetWidth || 1200;
      const gap = 24; // gap-6
      const maxPreview = containerW - MIN_FORM_WIDTH - gap;
      const next = Math.min(Math.max(startWidthRef.current + delta, MIN_PREVIEW_WIDTH), maxPreview);
      setPreviewWidth(next);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [previewWidth]);

  // Line items initialized from column config
  const [lineItems, setLineItems] = useState(() => {
    const cols = normalizeColumns(invoice?.column_config || savedColumns || null);
    if (invoice?.line_items?.length > 0) {
      return invoice.line_items.map((li) => {
        const line = {};
        for (const col of cols) {
          if (isComputed(col)) continue;
          line[col.key] = li[col.key] !== undefined ? li[col.key] : (col.default ?? '');
        }
        line.account_id = li.account_id;
        return line;
      });
    }
    return [buildEmptyLine(cols)];
  });

  // When columns change, sync lineItems to add/remove keys
  const handleColumnsChange = (newCols) => {
    setColumns(newCols);
    setLineItems((prev) => prev.map((li) => {
      const updated = {};
      for (const col of newCols) {
        if (isComputed(col)) continue;
        if (col.type === 'number' || col.type === 'currency' || col.type === 'percent') {
          updated[col.key] = li[col.key] !== undefined ? li[col.key] : (col.default ?? 0);
        } else if (col.type === 'boolean') {
          updated[col.key] = li[col.key] !== undefined ? li[col.key] : false;
        } else {
          updated[col.key] = li[col.key] !== undefined ? li[col.key] : (col.default ?? '');
        }
      }
      updated.account_id = li.account_id;
      return updated;
    }));
  };

  // Auto-calculate due date when terms or invoice date change
  useEffect(() => {
    const days = TERMS_TO_DAYS[terms];
    if (days !== undefined && invoiceDate) {
      setDueDate(addDays(invoiceDate, days));
    }
  }, [terms, invoiceDate]);

  // Find the subtotal column: prefer key='amount' with formula, else last formula column
  const subtotalCol = useMemo(() => {
    const amountCol = columns.find(c => c.key === 'amount' && isComputed(c));
    if (amountCol) return amountCol;
    const formulaCols = columns.filter(c => isComputed(c));
    return formulaCols[formulaCols.length - 1] || null;
  }, [columns]);

  const subtotal = useMemo(() => lineItems.reduce((s, li) => {
    if (subtotalCol) {
      const computed = computeRowValues(columns, li);
      return s + (computed[subtotalCol.key] || 0);
    }
    return s + ((Number(li.quantity) || 0) * (Number(li.unit_price) || 0));
  }, 0), [lineItems, columns, subtotalCol]);

  const taxAmt = useMemo(() => Math.round(subtotal * (Number(taxRate) / 100) * 100) / 100, [subtotal, taxRate]);
  const total = subtotal + taxAmt;

  const updateLine = (idx, field, value) => {
    setLineItems((prev) => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };
  const addLine = () => setLineItems((prev) => [...prev, buildEmptyLine(columns)]);
  const removeLine = (idx) => setLineItems((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  const columnsChanged = useMemo(() => JSON.stringify(columns) !== JSON.stringify(normalizeColumns(savedColumns)), [columns, savedColumns]);

  // Validation
  const descKey = columns[0]?.key;
  const emptyDescCount = lineItems.filter((li) => !(li[descKey] || '').trim()).length;
  const missingCustomer = !customerId;
  const hasValidationErrors = missingCustomer || emptyDescCount === lineItems.length;

  const doSave = (saveAsDefault) => {
    setShowSavePrompt(false);
    setShowValidation(false);
    onSave({
      customer_id: customerId,
      invoice_number: invoiceNumber,
      invoice_date: invoiceDate,
      due_date: dueDate,
      terms,
      tax_rate: Number(taxRate) / 100,
      notes,
      footer_note: footerNote,
      line_items: lineItems.filter((li) => (li.description || '').trim()),
      column_config: columns,
      template_id: selectedTemplateId || null,
    }, saveAsDefault ? columns : null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (hasValidationErrors) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    if (columnsChanged && !isEdit) {
      setShowSavePrompt(true);
      return;
    }
    doSave(false);
  };

  // PO import handler
  const handlePOImport = async (file) => {
    if (!file || file.type !== 'application/pdf') {
      setPOError('Please select a PDF file.');
      return;
    }
    if (file.size > 7.5 * 1024 * 1024) {
      setPOError('PDF is too large (max 7.5MB).');
      return;
    }

    setPOExtracting(true);
    setPOError(null);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await apiFetch('/api/sales-invoices/extract-po', {
        method: 'POST',
        body: JSON.stringify({ pdf_base64: base64 }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Extraction failed');

      const data = result.data;

      // 1. Customer matching
      if (data.customer_name) {
        const nameNorm = data.customer_name.toLowerCase().trim();
        const match = customers.find(c => {
          const dn = (c.display_name || '').toLowerCase();
          const cn = (c.company_name || '').toLowerCase();
          return dn === nameNorm || cn === nameNorm
            || dn.includes(nameNorm) || nameNorm.includes(dn)
            || cn.includes(nameNorm) || nameNorm.includes(cn);
        });
        if (match) setCustomerId(match.id);
      }

      // 2. PO number → notes
      if (data.po_number) {
        setNotes(prev => {
          const poRef = `PO# ${data.po_number}`;
          return prev ? `${poRef}\n${prev}` : poRef;
        });
      }

      // 3. Payment terms → terms dropdown
      if (data.payment_terms) {
        const termsLower = data.payment_terms.toLowerCase().replace(/\s+/g, '');
        const termsMap = {
          'net10': 'net_10', 'net15': 'net_15', 'net30': 'net_30',
          'net45': 'net_45', 'net60': 'net_60', 'net90': 'net_90',
          'dueonreceipt': 'due_on_receipt',
        };
        const mapped = termsMap[termsLower];
        if (mapped) setTerms(mapped);
      }

      // 4. Detect extra columns from PO data and add them dynamically
      if (data.line_items && data.line_items.length > 0) {
        // Map of PO fields → column definitions (inserted before quantity)
        const extraColumnDefs = [
          { poKey: 'part_number', key: 'part_number', label: 'Part No.', type: 'text' },
          { poKey: 'unit_of_measure', key: 'unit_of_measure', label: 'Unit', type: 'text' },
          { poKey: 'discount', key: 'discount', label: 'Discount', type: 'currency' },
          { poKey: 'delivery_date', key: 'delivery_date', label: 'Delivery Date', type: 'date' },
          { poKey: 'line_notes', key: 'line_notes', label: 'Notes', type: 'text' },
        ];

        // Check which extra columns have data in any line item
        const colsToAdd = extraColumnDefs.filter(def =>
          data.line_items.some(li => li[def.poKey] != null && li[def.poKey] !== '')
        );

        // Build updated columns — insert new ones before quantity (index 1)
        let updatedColumns = [...columns];
        for (const def of colsToAdd) {
          if (!updatedColumns.find(c => c.key === def.key)) {
            const descIdx = updatedColumns.findIndex(c => c.key === 'description');
            const insertAt = descIdx >= 0 ? descIdx + 1 : 1;
            updatedColumns.splice(insertAt, 0, { key: def.key, label: def.label, type: def.type });
          }
        }

        // Move unit_of_measure right after quantity if both exist
        const uomIdx = updatedColumns.findIndex(c => c.key === 'unit_of_measure');
        const qtyIdx = updatedColumns.findIndex(c => c.key === 'quantity');
        if (uomIdx >= 0 && qtyIdx >= 0 && uomIdx !== qtyIdx + 1) {
          const [uomCol] = updatedColumns.splice(uomIdx, 1);
          const newQtyIdx = updatedColumns.findIndex(c => c.key === 'quantity');
          updatedColumns.splice(newQtyIdx + 1, 0, uomCol);
        }

        if (updatedColumns.length !== columns.length) {
          setColumns(updatedColumns);
        }

        // Build line items with all columns populated
        const newLines = data.line_items.map(li => {
          const line = buildEmptyLine(updatedColumns);
          line.description = li.description || '';
          line.quantity = li.quantity != null ? li.quantity : 1;
          if (li.unit_price != null) line.unit_price = li.unit_price;
          if (li.part_number) line.part_number = li.part_number;
          if (li.unit_of_measure) line.unit_of_measure = li.unit_of_measure;
          if (li.discount != null) line.discount = li.discount;
          if (li.delivery_date) line.delivery_date = li.delivery_date;
          if (li.line_notes) line.line_notes = li.line_notes;
          return line;
        });
        setLineItems(newLines);
      }

      // 5. Append any PO notes/instructions
      if (data.notes) {
        setNotes(prev => prev ? `${prev}\n${data.notes}` : data.notes);
      }
    } catch (err) {
      console.error('PO import failed:', err);
      setPOError(err.message || 'Failed to extract PO data. Please try again.');
    } finally {
      setPOExtracting(false);
      if (poFileRef.current) poFileRef.current.value = '';
    }
  };

  const selectedCustomer = customers.find((c) => c.id === customerId);

  // Build a preview invoice object for live PDF rendering
  const previewInvoice = useMemo(() => ({
    invoice_number: invoiceNumber || 'DRAFT',
    invoice_date: invoiceDate,
    due_date: dueDate,
    terms,
    subtotal,
    tax_rate: Number(taxRate) / 100,
    tax_amount: taxAmt,
    total_amount: total,
    balance_due: total - (invoice?.amount_paid || 0),
    amount_paid: invoice?.amount_paid || 0,
    notes,
    footer_note: footerNote,
    column_config: columns,
    line_items: lineItems.map((li, i) => ({
      id: i,
      ...li,
      ...computeRowValues(columns, li),
    })),
    customers: selectedCustomer || {},
  }), [invoiceNumber, invoiceDate, dueDate, terms, subtotal, taxRate, taxAmt, total, notes, footerNote, columns, lineItems, selectedCustomer, invoice?.amount_paid]);

  // Render a cell input based on column type
  const renderCell = (col, li, idx, isFirst, rowComputed) => {
    const borderCls = isFirst ? '' : 'border-l border-zinc-100';
    if (isComputed(col)) {
      const value = rowComputed?.[col.key] || 0;
      const formatted = col.type === 'currency' ? fmt(value) : col.type === 'percent' ? `${value}%` : value;
      return <div key={col.key} className={`px-3 py-2.5 text-sm text-right truncate min-w-0 ${borderCls} text-zinc-600`}>{formatted}</div>;
    }
    if (col.type === 'text') {
      const isEmpty = !(li[col.key] || '').trim();
      const isDesc = col.key === descKey;
      const errorCls = showValidation && isDesc && isEmpty ? 'ring-2 ring-red-400 bg-red-50/50' : '';
      return (
        <input key={col.key} type="text" value={li[col.key] || ''} onChange={(e) => { updateLine(idx, col.key, e.target.value); if (showValidation) setShowValidation(false); }}
          placeholder={col.label} className={`px-3 py-2.5 text-sm border-0 focus:outline-none focus:bg-zinc-50 min-w-0 w-full ${borderCls} ${errorCls}`} />
      );
    }
    if (col.type === 'number') {
      return (
        <input key={col.key} type="number" value={li[col.key] ?? ''} onChange={(e) => updateLine(idx, col.key, e.target.value)}
          min="0" step="any" className={`px-3 py-2.5 text-sm text-right focus:outline-none focus:bg-zinc-50 min-w-0 w-full ${borderCls}`} />
      );
    }
    if (col.type === 'currency') {
      return (
        <input key={col.key} type="number" value={li[col.key] ?? ''} onChange={(e) => updateLine(idx, col.key, e.target.value)}
          min="0" step="0.01" className={`px-3 py-2.5 text-sm text-right focus:outline-none focus:bg-zinc-50 min-w-0 w-full ${borderCls}`} />
      );
    }
    if (col.type === 'percent') {
      return (
        <div key={col.key} className={`flex items-center min-w-0 ${borderCls}`}>
          <input type="number" value={li[col.key] ?? ''} onChange={(e) => updateLine(idx, col.key, e.target.value)}
            min="0" max="100" step="0.01" className="w-full px-2 py-2.5 text-sm text-right focus:outline-none focus:bg-zinc-50 min-w-0" />
          <span className="text-xs text-zinc-400 pr-2 shrink-0">%</span>
        </div>
      );
    }
    if (col.type === 'date') {
      return (
        <input key={col.key} type="date" value={li[col.key] || ''} onChange={(e) => updateLine(idx, col.key, e.target.value)}
          className={`px-2 py-2.5 text-xs focus:outline-none focus:bg-zinc-50 min-w-0 w-full ${borderCls}`} />
      );
    }
    if (col.type === 'boolean') {
      return (
        <div key={col.key} className={`flex items-center justify-center ${borderCls}`}>
          <input type="checkbox" checked={!!li[col.key]} onChange={(e) => updateLine(idx, col.key, e.target.checked)}
            className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900" />
        </div>
      );
    }
    return <div key={col.key} className={borderCls} />;
  };

  return (
    <div ref={containerRef} className="flex gap-6 items-start w-full overflow-hidden">
      <form onSubmit={handleSubmit} className="flex-1 overflow-hidden" style={{ minWidth: `${MIN_FORM_WIDTH}px` }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-zinc-900">{isEdit ? `Edit ${invoice.invoice_number}` : 'New Invoice'}</h2>
        <div className="flex items-center gap-2">
          {templates && templates.length > 0 && (
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white"
            >
              <option value="">No template</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.is_default ? ' (Default)' : ''}
                </option>
              ))}
            </select>
          )}
          {!isEdit && (
            <>
              <input
                ref={poFileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) handlePOImport(e.target.files[0]); }}
              />
              <button
                type="button"
                onClick={() => poFileRef.current?.click()}
                disabled={poExtracting}
                className="px-4 py-2 text-sm border border-zinc-200 rounded-md text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                {poExtracting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M8 1v10M4 5l4-4 4 4" />
                      <path d="M2 11v3h12v-3" />
                    </svg>
                    Import from PO
                  </>
                )}
              </button>
            </>
          )}
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-5 py-2 bg-zinc-900 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {saving ? 'Saving...' : isEdit ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      </div>

      {/* PO import error */}
      {poError && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 mb-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="shrink-0">
            <circle cx="8" cy="8" r="7" />
            <path d="M8 5v4" strokeLinecap="round" />
            <circle cx="8" cy="11.5" r="0.5" fill="currentColor" />
          </svg>
          {poError}
          <button type="button" onClick={() => setPOError(null)} className="ml-auto text-amber-500 hover:text-amber-700">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
          </button>
        </div>
      )}

      {/* Validation errors */}
      {showValidation && hasValidationErrors && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0 mt-0.5">
            <circle cx="10" cy="10" r="9" stroke="#ef4444" strokeWidth="1.5" />
            <path d="M10 6v5" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="10" cy="14" r="1" fill="#ef4444" />
          </svg>
          <div className="text-sm text-red-700 space-y-1">
            {missingCustomer && <p>Please select a customer before creating the invoice.</p>}
            {emptyDescCount > 0 && (
              <p>Product description missing for {emptyDescCount} {emptyDescCount === 1 ? 'item' : 'items'}! Add a description to at least one line item.</p>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-zinc-200 p-6 space-y-6">
        {/* Customer + Invoice Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Customer */}
          <div>
            <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Bill To</label>
            <select
              value={customerId}
              onChange={(e) => {
                if (e.target.value === '__new__') {
                  setShowNewCustomer(true);
                  setCustomerId('');
                } else {
                  setCustomerId(e.target.value);
                  setShowNewCustomer(false);
                  if (showValidation) setShowValidation(false);
                }
              }}
              className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white"
              required={!showNewCustomer}
            >
              <option value="">Select a customer...</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.display_name}</option>
              ))}
              <option value="__new__">+ Add new customer</option>
            </select>

            {/* Inline new customer form */}
            {showNewCustomer && (
              <div className="mt-3 p-3 border border-zinc-200 rounded-md bg-zinc-50 space-y-2">
                <p className="text-xs font-medium text-zinc-600 mb-2">Quick-create customer</p>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" value={newCustFirstName} onChange={(e) => setNewCustFirstName(e.target.value)} placeholder="First name" className="px-3 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white" />
                  <input type="text" value={newCustLastName} onChange={(e) => setNewCustLastName(e.target.value)} placeholder="Last name" className="px-3 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white" />
                </div>
                <input type="text" value={newCustCompany} onChange={(e) => setNewCustCompany(e.target.value)} placeholder="Company name *" className="w-full px-3 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="email" value={newCustEmail} onChange={(e) => setNewCustEmail(e.target.value)} placeholder="Email" className="px-3 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white" />
                  <input type="tel" value={newCustPhone} onChange={(e) => setNewCustPhone(e.target.value)} placeholder="Phone" className="px-3 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white" />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    disabled={creatingCustomer || !newCustCompany.trim()}
                    onClick={async () => {
                      setCreatingCustomer(true);
                      try {
                        const displayName = newCustCompany.trim() || [newCustFirstName, newCustLastName].filter(Boolean).join(' ');
                        const res = await apiFetch('/api/customers', { method: 'POST', body: JSON.stringify({ display_name: displayName, first_name: newCustFirstName.trim() || null, last_name: newCustLastName.trim() || null, company_name: newCustCompany.trim() || null, email: newCustEmail.trim() || null, phone: newCustPhone.trim() || null }) });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data.error);
                        setCustomerId(data.customer.id);
                        setShowNewCustomer(false);
                        setNewCustFirstName(''); setNewCustLastName(''); setNewCustCompany(''); setNewCustEmail(''); setNewCustPhone('');
                        if (onCustomerCreated) onCustomerCreated(data.customer);
                      } catch (err) { alert(err.message); } finally { setCreatingCustomer(false); }
                    }}
                    className="px-3 py-1.5 bg-zinc-900 text-white text-xs rounded hover:bg-zinc-800 disabled:opacity-40 transition-colors"
                  >
                    {creatingCustomer ? 'Creating...' : 'Create & Select'}
                  </button>
                  <button type="button" onClick={() => setShowNewCustomer(false)} className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-700 transition-colors">Cancel</button>
                </div>
              </div>
            )}

            {selectedCustomer && !showNewCustomer && (
              <div className="mt-2 text-xs text-zinc-400 leading-relaxed">
                {selectedCustomer.company_name && <div>{selectedCustomer.company_name}</div>}
                {selectedCustomer.email && <div>{selectedCustomer.email}</div>}
                {selectedCustomer.address_line1 && <div>{selectedCustomer.address_line1}</div>}
                {(selectedCustomer.city || selectedCustomer.state || selectedCustomer.zip) && (
                  <div>{[selectedCustomer.city, selectedCustomer.state].filter(Boolean).join(', ')} {selectedCustomer.zip}</div>
                )}
              </div>
            )}
          </div>

          {/* Right: Invoice details */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1">Invoice No.</label>
                <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Auto-generated" className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1">Terms</label>
                <select value={terms} onChange={(e) => setTerms(e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white">
                  {TERMS_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1">Invoice Date</label>
                <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1">Due Date</label>
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900" />
              </div>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider">Line Items</label>
            <button
              type="button"
              onClick={() => setShowColumnEditor(!showColumnEditor)}
              className="text-xs text-zinc-400 hover:text-zinc-700 transition-colors flex items-center gap-1"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13.5 2.5l-1-1-9 9-.5 2.5 2.5-.5 9-9-1-1Z" /></svg>
              {showColumnEditor ? 'Hide columns' : 'Customize columns'}
            </button>
          </div>

          {showColumnEditor && <ColumnEditor columns={columns} onChange={handleColumnsChange} />}

          <div className="border border-zinc-200 rounded-md overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
            <div style={{ width: 'max-content', minWidth: '100%' }}>
              {/* Dynamic Header */}
              <div className="grid gap-x-1 bg-zinc-50 border-b border-zinc-200 text-xs font-medium text-zinc-500 uppercase tracking-wider" style={{ gridTemplateColumns: gridCols(columns, '36px') }}>
                {columns.map((col) => (
                  <div key={col.key} className={`px-3 py-2 leading-tight break-all ${col.type !== 'text' ? 'text-right' : ''}`}>{col.label}</div>
                ))}
                <div />
              </div>
              {/* Dynamic Rows */}
              {lineItems.map((li, idx) => {
                const rowComputed = computeRowValues(columns, li);
                return (
                  <div key={idx} className="grid gap-x-1 border-b border-zinc-100 last:border-b-0 items-center" style={{ gridTemplateColumns: gridCols(columns, '36px') }}>
                    {columns.map((col, colIdx) => renderCell(col, li, idx, colIdx === 0, rowComputed))}
                    <button type="button" onClick={() => removeLine(idx)} className="flex items-center justify-center h-full text-zinc-300 hover:text-red-500 transition-colors border-l border-zinc-100" title="Remove line">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <button type="button" onClick={addLine} className="mt-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 3v10M3 8h10" /></svg>
            Add line item
          </button>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Subtotal</span>
              <span className="text-zinc-900">{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm items-center gap-2">
              <span className="text-zinc-500">Tax</span>
              <div className="flex items-center gap-1">
                <input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} min="0" max="100" step="0.01" className="w-16 px-2 py-1 text-right text-xs border border-zinc-200 rounded focus:outline-none focus:ring-1 focus:ring-zinc-900" />
                <span className="text-xs text-zinc-400">%</span>
                <span className="text-zinc-900 ml-2">{fmt(taxAmt)}</span>
              </div>
            </div>
            <div className="flex justify-between text-sm font-semibold border-t border-zinc-200 pt-2">
              <span className="text-zinc-900">Total</span>
              <span className="text-zinc-900">{fmt(total)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
          <div>
            <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1">Notes / Memo</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Visible to customer on invoice" className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1">Footer / Terms</label>
            <textarea value={footerNote} onChange={(e) => setFooterNote(e.target.value)} rows={3} placeholder="e.g. Thank you for your business!" className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 resize-none" />
          </div>
        </div>
      </div>

      {/* Save-as-default prompt */}
      {showSavePrompt && <SaveDefaultPrompt onSave={() => doSave(true)} onSkip={() => doSave(false)} />}
    </form>

      {/* Live Invoice Preview — visible on xl+ screens */}
      <div
        className="hidden xl:flex sticky top-4 overflow-hidden shrink-0"
        style={{
          width: previewCollapsed ? '40px' : `${previewWidth}px`,
          height: previewCollapsed ? 'auto' : 'calc(100vh - 40px)',
        }}
      >
        {/* Drag handle — left edge */}
        {!previewCollapsed && (
          <div
            onMouseDown={onDragStart}
            className="w-1.5 shrink-0 cursor-col-resize hover:bg-zinc-300 bg-zinc-200 transition-colors self-stretch rounded-l-lg"
            title="Drag to resize"
          />
        )}
        <div className={`flex flex-col flex-1 min-w-0 border border-zinc-200 bg-white overflow-hidden ${previewCollapsed ? 'rounded-lg' : 'rounded-r-lg border-l-0'}`}>
          <InvoicePreview
            invoice={previewInvoice}
            companyName={companyName}
            collapsed={previewCollapsed}
            onToggleCollapse={() => setPreviewCollapsed(c => !c)}
            templateConfig={activeTemplateConfig}
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Payment Modal
// ---------------------------------------------------------------------------
const PaymentModal = ({ invoice, onRecord, onClose, saving }) => {
  const [amount, setAmount] = useState(invoice.balance_due > 0 ? invoice.balance_due.toFixed(2) : '');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [method, setMethod] = useState('ach');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onRecord({ amount: Number(amount), payment_date: paymentDate, method, reference_number: reference, notes });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-1">Record Payment</h3>
          <p className="text-sm text-zinc-400 mb-5">
            {invoice.invoice_number} &middot; Balance due: {fmt(invoice.balance_due)}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Amount</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} min="0.01" step="0.01" required className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Date</label>
                <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-white">
                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Reference #</label>
              <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Check number, ACH ref, etc." className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900" />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Notes</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Cancel</button>
              <button type="submit" disabled={saving || !amount || Number(amount) <= 0} className="px-5 py-2 bg-zinc-900 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-40 transition-colors">
                {saving ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Invoice Detail View
// ---------------------------------------------------------------------------
const InvoiceDetail = ({ invoiceId, onBack, onEdit, customers }) => {
  const { activeCompany } = useAuth();
  const companyName = activeCompany?.company?.name || 'My Company';
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/sales-invoices/${invoiceId}`);
      const data = await res.json();
      setInvoice(data.invoice);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handleSend = async () => {
    if (!window.confirm('Mark this invoice as sent?')) return;
    setActionLoading(true);
    try {
      await apiFetch(`/api/sales-invoices/${invoiceId}/send`, { method: 'POST' });
      setMsg({ type: 'success', text: 'Invoice marked as sent' });
      fetchInvoice();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleVoid = async () => {
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    setActionLoading(true);
    try {
      await apiFetch(`/api/sales-invoices/${invoiceId}/void`, { method: 'POST' });
      setMsg({ type: 'success', text: 'Invoice voided' });
      fetchInvoice();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecordPayment = async (fields) => {
    setActionLoading(true);
    try {
      await apiFetch(`/api/sales-invoices/${invoiceId}/payments`, { method: 'POST', body: JSON.stringify(fields) });
      setMsg({ type: 'success', text: 'Payment recorded' });
      setShowPayment(false);
      fetchInvoice();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await apiFetch(`/api/sales-invoices/${invoiceId}/payments/${paymentId}`, { method: 'DELETE' });
      fetchInvoice();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await pdf(<InvoicePDF invoice={invoice} companyName={companyName} templateConfig={invoice.template_config} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to generate PDF: ' + err.message });
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!invoice) {
    return <div className="text-center py-12 text-zinc-400">Invoice not found</div>;
  }

  const cust = invoice.customers;
  const canEdit = invoice.status === 'draft';
  const canSend = invoice.status === 'draft';
  const canVoid = !invoice.voided_at;
  const canPay = ['sent', 'partial', 'overdue'].includes(invoice.status);

  return (
    <div className="max-w-6xl">
      {/* Messages */}
      {msg && (
        <div className={`mb-4 px-4 py-2 rounded-md text-sm ${msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 hover:bg-zinc-100 rounded-md transition-colors">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M10 3L5 8l5 5" /></svg>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-zinc-900">{invoice.invoice_number}</h2>
              <StatusBadge status={invoice.status} />
            </div>
            <p className="text-sm text-zinc-400">{cust?.display_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            className="px-3 py-1.5 text-sm border border-zinc-200 rounded-md hover:border-zinc-400 transition-colors flex items-center gap-1.5 disabled:opacity-40"
          >
            {pdfLoading ? (
              <div className="w-3.5 h-3.5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
            ) : (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 2v8M5 7l3 3 3-3" />
                <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" />
              </svg>
            )}
            PDF
          </button>
          {canEdit && (
            <button onClick={() => onEdit(invoice)} className="px-3 py-1.5 text-sm border border-zinc-200 rounded-md hover:border-zinc-400 transition-colors" disabled={actionLoading}>Edit</button>
          )}
          {canSend && (
            <button onClick={handleSend} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" disabled={actionLoading}>
              Mark as Sent
            </button>
          )}
          {canPay && (
            <button onClick={() => setShowPayment(true)} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors" disabled={actionLoading}>
              Record Payment
            </button>
          )}
          {canVoid && invoice.status !== 'paid' && (
            <button onClick={handleVoid} className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors" disabled={actionLoading}>
              Void
            </button>
          )}
        </div>
      </div>

      {/* Invoice card */}
      <div className="bg-white rounded-lg border border-zinc-200 p-6 overflow-hidden">
        {/* Customer + Invoice info */}
        <div className="grid grid-cols-2 gap-6 mb-6 pb-6 border-b border-zinc-100">
          <div>
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Bill To</p>
            <p className="text-sm font-medium text-zinc-900">{cust?.display_name}</p>
            {cust?.company_name && <p className="text-xs text-zinc-500">{cust.company_name}</p>}
            {cust?.email && <p className="text-xs text-zinc-400">{cust.email}</p>}
            {cust?.address_line1 && <p className="text-xs text-zinc-400">{cust.address_line1}</p>}
            {(cust?.city || cust?.state) && (
              <p className="text-xs text-zinc-400">{[cust.city, cust.state].filter(Boolean).join(', ')} {cust.zip}</p>
            )}
          </div>
          <div className="text-right space-y-1">
            <div className="text-xs text-zinc-400">Invoice Date: <span className="text-zinc-700">{fmtDate(invoice.invoice_date)}</span></div>
            <div className="text-xs text-zinc-400">Due Date: <span className="text-zinc-700">{fmtDate(invoice.due_date)}</span></div>
            <div className="text-xs text-zinc-400">Terms: <span className="text-zinc-700">{TERMS_OPTIONS.find((t) => t.value === invoice.terms)?.label || invoice.terms}</span></div>
          </div>
        </div>

        {/* Line Items — dynamic columns */}
        {(() => {
          const cols = normalizeColumns(invoice.column_config);
          return (
            <div className="mb-6 overflow-x-auto rounded-md border border-zinc-200">
              <div>
                <div className="grid gap-x-3 bg-zinc-50 border-b border-zinc-200 px-3 py-2 text-xs font-medium text-zinc-500 uppercase tracking-wider" style={{ gridTemplateColumns: gridCols(cols) }}>
                  {cols.map((col) => (
                    <div key={col.key} className={`truncate ${col.type === 'text' ? '' : 'text-right'}`}>{col.label}</div>
                  ))}
                </div>
                <div className="divide-y divide-zinc-100">
                  {invoice.line_items?.map((li) => (
                    <div key={li.id} className="grid gap-x-3 px-3 py-2.5 text-sm" style={{ gridTemplateColumns: gridCols(cols) }}>
                      {cols.map((col) => (
                        <div key={col.key} className={`truncate min-w-0 ${col.type === 'text' ? 'text-zinc-900' : 'text-right text-zinc-600'}`}>
                          {formatCellValue(col, li[col.key])}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Totals */}
        <div className="flex justify-end mb-6">
          <div className="w-64 space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-zinc-500">Subtotal</span><span className="text-zinc-700">{fmt(invoice.subtotal)}</span></div>
            {Number(invoice.tax_amount) > 0 && (
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Tax ({(Number(invoice.tax_rate) * 100).toFixed(1)}%)</span><span className="text-zinc-700">{fmt(invoice.tax_amount)}</span></div>
            )}
            <div className="flex justify-between text-sm font-semibold border-t border-zinc-200 pt-1.5"><span className="text-zinc-900">Total</span><span className="text-zinc-900">{fmt(invoice.total_amount)}</span></div>
            {invoice.amount_paid > 0 && (
              <div className="flex justify-between text-sm"><span className="text-emerald-600">Paid</span><span className="text-emerald-600">-{fmt(invoice.amount_paid)}</span></div>
            )}
            {invoice.balance_due > 0 && (
              <div className="flex justify-between text-sm font-semibold border-t border-zinc-200 pt-1.5"><span className="text-zinc-900">Balance Due</span><span className={invoice.status === 'overdue' ? 'text-red-600' : 'text-zinc-900'}>{fmt(invoice.balance_due)}</span></div>
            )}
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="pt-4 border-t border-zinc-100">
            <p className="text-xs text-zinc-400 mb-1">Notes</p>
            <p className="text-sm text-zinc-600 whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        )}
        {invoice.footer_note && (
          <div className="pt-3">
            <p className="text-xs text-zinc-400 mb-1">Terms</p>
            <p className="text-sm text-zinc-600 whitespace-pre-wrap">{invoice.footer_note}</p>
          </div>
        )}
      </div>

      {/* Payment History */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-zinc-200 p-6">
          <h3 className="text-sm font-semibold text-zinc-900 mb-3">Payment History</h3>
          <div className="space-y-2">
            {invoice.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-zinc-50 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-emerald-600"><path d="M2 8l4 4L14 4" /></svg>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-900">{fmt(p.amount)}</p>
                    <p className="text-xs text-zinc-400">
                      {fmtDate(p.payment_date)} &middot; {PAYMENT_METHODS.find((m) => m.value === p.method)?.label || p.method}
                      {p.reference_number && <> &middot; Ref: {p.reference_number}</>}
                    </p>
                  </div>
                </div>
                <button onClick={() => handleDeletePayment(p.id)} className="text-zinc-300 hover:text-red-500 transition-colors p-1" title="Delete payment">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 5h10M5.5 5V3.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V5M6.5 7.5v4M9.5 7.5v4" /><path d="M4 5l.5 8a1.5 1.5 0 0 0 1.5 1.5h4a1.5 1.5 0 0 0 1.5-1.5L12 5" /></svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && <PaymentModal invoice={invoice} onRecord={handleRecordPayment} onClose={() => setShowPayment(false)} saving={actionLoading} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main InvoicingView
// ---------------------------------------------------------------------------
const InvoicingView = ({ loaded }) => {
  const { activeCompany } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState({ totalOutstanding: 0, totalOverdue: 0, totalRevenue: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  // View state: 'list' | 'detail' | 'form'
  const [view, setView] = useState('list');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null); // full invoice obj for editing

  // Saved column config from company settings
  const savedColumns = activeCompany?.company?.invoice_settings?.line_columns || null;

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [invRes, custRes, sumRes, tplRes] = await Promise.all([
        apiFetch('/api/sales-invoices'),
        apiFetch('/api/customers'),
        apiFetch('/api/sales-invoices/summary'),
        apiFetch('/api/invoice-templates'),
      ]);
      const invData = await invRes.json();
      const custData = await custRes.json();
      const sumData = await sumRes.json();
      const tplData = await tplRes.json();

      setInvoices(invData.invoices || []);
      setCustomers(custData.customers || []);
      setSummary(sumData);
      setTemplates(tplData.templates || []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter + sort invoices
  const filteredInvoices = useMemo(() => {
    let list = [...invoices];

    if (statusFilter !== 'all') {
      list = list.filter((i) => i.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((i) =>
        i.invoice_number?.toLowerCase().includes(q) ||
        i.customers?.display_name?.toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc': return new Date(a.invoice_date) - new Date(b.invoice_date);
        case 'date_desc': return new Date(b.invoice_date) - new Date(a.invoice_date);
        case 'amount_asc': return (Number(a.total_amount) || 0) - (Number(b.total_amount) || 0);
        case 'amount_desc': return (Number(b.total_amount) || 0) - (Number(a.total_amount) || 0);
        case 'due_date': return new Date(a.due_date) - new Date(b.due_date);
        default: return 0;
      }
    });

    return list;
  }, [invoices, statusFilter, search, sortBy]);

  // Handlers
  const handleCreate = () => {
    setEditingInvoice(null);
    setView('form');
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setView('form');
  };

  const handleSave = async (fields, saveColumnsAsDefault) => {
    setSaving(true);
    try {
      // Save columns as company default if user opted in
      if (saveColumnsAsDefault) {
        const settings = { ...(activeCompany?.company?.invoice_settings || {}), line_columns: saveColumnsAsDefault };
        await apiFetch('/api/companies', { method: 'PUT', body: JSON.stringify({ invoice_settings: settings }) });
      }

      if (editingInvoice) {
        await apiFetch(`/api/sales-invoices/${editingInvoice.id}`, { method: 'PUT', body: JSON.stringify(fields) });
        setMsg({ type: 'success', text: 'Invoice updated' });
      } else {
        const res = await apiFetch('/api/sales-invoices', { method: 'POST', body: JSON.stringify(fields) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to create invoice');
        setMsg({ type: 'success', text: `Invoice ${data.invoice?.invoice_number || ''} created` });
      }
      setView('list');
      fetchData();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('Seed demo customers and invoices? This only works if you have no sales invoices yet.')) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/sales-invoices/seed', { method: 'POST' });
      const data = await res.json();
      if (data.seeded) {
        setMsg({ type: 'success', text: `Seeded ${data.customers} customers and ${data.invoices} invoices` });
        fetchData();
      } else {
        setMsg({ type: 'error', text: data.message || 'Already seeded' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (id) => {
    setSelectedInvoiceId(id);
    setView('detail');
  };

  // ---------------------------------------------------------------------------
  // Render: Loading
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center py-20">
          <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Form view
  // ---------------------------------------------------------------------------
  if (view === 'form') {
    return (
      <div className="mx-auto overflow-hidden" style={{ maxWidth: '1800px' }}>
        <InvoiceForm
          invoice={editingInvoice}
          customers={customers}
          onSave={handleSave}
          onCancel={() => { setView(editingInvoice ? 'detail' : 'list'); }}
          saving={saving}
          onCustomerCreated={(newCust) => setCustomers((prev) => [...prev, newCust].sort((a, b) => a.display_name.localeCompare(b.display_name)))}
          savedColumns={savedColumns}
          companyName={activeCompany?.company?.name || 'My Company'}
          templates={templates}
        />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Detail view
  // ---------------------------------------------------------------------------
  if (view === 'detail' && selectedInvoiceId) {
    return (
      <div className="max-w-6xl mx-auto">
        <InvoiceDetail
          invoiceId={selectedInvoiceId}
          onBack={() => { setView('list'); fetchData(); }}
          onEdit={handleEdit}
          customers={customers}
        />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: List view
  // ---------------------------------------------------------------------------
  const statusCounts = {};
  invoices.forEach((i) => { statusCounts[i.status] = (statusCounts[i.status] || 0) + 1; });

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div
        className="flex items-center justify-between mb-6 transition-all duration-700"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '200ms' }}
      >
        <div>
          <h1 className="text-2xl font-light text-zinc-900">Invoicing</h1>
          <p className="text-sm text-zinc-400">Create, send, and track customer invoices</p>
        </div>
        <button onClick={handleCreate} className="px-4 py-2 bg-zinc-900 text-white text-sm rounded-md hover:bg-zinc-800 transition-colors flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v10M3 8h10" /></svg>
          New Invoice
        </button>
      </div>

      {/* Messages */}
      {msg && (
        <div className={`mb-4 px-4 py-2 rounded-md text-sm ${msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Empty state */}
      {invoices.length === 0 ? (
        <div
          className="text-center py-16 transition-all duration-700"
          style={{ opacity: loaded ? 1 : 0, transitionDelay: '400ms' }}
        >
          <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 3H7a2 2 0 0 0-2 2v18a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V10l-7-7Z" />
              <path d="M16 3v7h7" />
              <path d="M10 15h8M10 19h5" />
            </svg>
          </div>
          <h2 className="text-lg font-medium text-zinc-900 mb-2">No invoices yet</h2>
          <p className="text-sm text-zinc-400 mb-6">Create your first invoice or seed demo data to get started.</p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={handleCreate} className="px-5 py-2.5 bg-zinc-900 text-white text-sm rounded-md hover:bg-zinc-800 transition-colors">
              Create Invoice
            </button>
            <button onClick={handleSeed} disabled={saving} className="px-5 py-2.5 border border-zinc-200 text-zinc-600 text-sm rounded-md hover:border-zinc-400 transition-colors disabled:opacity-40">
              {saving ? 'Seeding...' : 'Seed Demo Data'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <SummaryCards summary={summary} loaded={loaded} />

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            {/* Status tabs */}
            <div className="flex items-center gap-1 bg-zinc-100 rounded-md p-0.5">
              {[
                { key: 'all', label: 'All' },
                { key: 'draft', label: 'Draft' },
                { key: 'sent', label: 'Sent' },
                { key: 'overdue', label: 'Overdue' },
                { key: 'partial', label: 'Partial' },
                { key: 'paid', label: 'Paid' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                    statusFilter === tab.key
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  {tab.label}
                  {tab.key !== 'all' && statusCounts[tab.key] ? ` (${statusCounts[tab.key]})` : ''}
                </button>
              ))}
            </div>

            {/* Search + Sort */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="7" cy="7" r="5" /><path d="M11 11L14 14" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search invoices..."
                  className="pl-8 pr-3 py-1.5 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-zinc-900 w-48"
                />
              </div>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-2 py-1.5 text-xs border border-zinc-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-zinc-900">
                <option value="date_desc">Newest first</option>
                <option value="date_asc">Oldest first</option>
                <option value="amount_desc">Amount (high)</option>
                <option value="amount_asc">Amount (low)</option>
                <option value="due_date">Due date</option>
              </select>
            </div>
          </div>

          {/* Invoice Table */}
          <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[1fr_140px_100px_100px_100px_90px] gap-0 bg-zinc-50 border-b border-zinc-200 px-4 py-2.5 text-xs font-medium text-zinc-500 uppercase tracking-wider">
              <div>Invoice</div>
              <div>Customer</div>
              <div className="text-right">Amount</div>
              <div className="text-right">Balance</div>
              <div className="text-center">Status</div>
              <div className="text-right">Date</div>
            </div>

            {/* Rows */}
            {filteredInvoices.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-zinc-400">No invoices match your filters</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {filteredInvoices.map((inv) => (
                  <button
                    key={inv.id}
                    onClick={() => openDetail(inv.id)}
                    className="w-full grid grid-cols-[1fr_140px_100px_100px_100px_90px] gap-0 px-4 py-3 text-left hover:bg-zinc-50 transition-colors items-center"
                  >
                    <div>
                      <span className="text-sm font-medium text-zinc-900">{inv.invoice_number}</span>
                    </div>
                    <div className="text-sm text-zinc-600 truncate">{inv.customers?.display_name || '—'}</div>
                    <div className="text-sm text-zinc-900 text-right">{fmt(inv.total_amount)}</div>
                    <div className={`text-sm text-right ${inv.balance_due > 0 ? (inv.status === 'overdue' ? 'text-red-600 font-medium' : 'text-zinc-600') : 'text-zinc-300'}`}>
                      {inv.balance_due > 0 ? fmt(inv.balance_due) : '—'}
                    </div>
                    <div className="text-center"><StatusBadge status={inv.status} /></div>
                    <div className="text-xs text-zinc-400 text-right">{fmtDate(inv.invoice_date)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer info */}
          <div className="mt-3 text-xs text-zinc-400 text-right">
            {filteredInvoices.length} of {invoices.length} invoices
          </div>
        </>
      )}
    </div>
  );
};

export default InvoicingView;
